import { useEffect } from 'react';
import { CallbackInterface, useRecoilCallback } from 'recoil';
import { useSessionOrNull } from '~/contexts/SessionContext';
import { DomainChange } from '~/domain/changes/DomainChange';
import type { StructureSet } from '~/domain/patient/structureset';
import { createStructureSetReadModel } from '~/domain/readmodels/StructureSetReadModel';
import {
    currentStructureSetIdState,
    structureSetReadModelFamily,
} from '~/state/atoms/structureSet/structureSetAtoms';
import { domainTrace } from '~/domain/diagnostics/domainTrace';

/** Rebuilds `ss`'s read model from the live entity and writes it to its family entry. */
function publishReadModel(
    set: CallbackInterface['set'],
    ss: StructureSet,
    type: DomainChange['type'] | 'attached'
): void {
    const readModel = createStructureSetReadModel(ss);
    domainTrace.bridge('atom update', {
        family: 'structureSetReadModelFamily',
        ssDicomUid: readModel.dicomUid,
        type,
    });
    set(structureSetReadModelFamily(readModel.dicomUid), readModel);
}

/**
 * Renderless React component that mirrors domain-layer changesets into the
 * legacy Recoil atoms so that components which still read from those atoms
 * stay in sync with the new write path.
 *
 * **Coverage map** (kept up to date as legacy atoms get retired):
 *
 * - `structureSetReadModelFamily(uid)`: written for every structure set
 *   when a session attaches, and again for every SS / ROI change, so the
 *   state a session adopts before its first changeset reaches the atoms
 *   too. Any selector derived from it (notably `currentStructureSetState`,
 *   `roisVisibilityState`, `roiColorsState`, `roisForDvhState`,
 *   `selectedRoiIsPointTypeState`) recomputes automatically. We do NOT
 *   mirror those selectors directly.
 * - `currentStructureSetIdState`: cleared when the currently selected SS
 *   is deleted in the domain layer so direct readers (export step,
 *   dataset list, handlers) don't hold a dangling UID.
 *
 * **Migration note:** This bridge exists only during the migration phase.
 * Once all legacy readers have been ported to projection hooks the bridge
 * can be deleted.
 *
 * Mounted once in the App root (Task 26). Returns null.
 */
export function RecoilBridge() {
    const session = useSessionOrNull();

    // useRecoilCallback gives us a `set` that is stable across renders and
    // does NOT subscribe this component — so atom updates never cause a
    // self-induced re-render.
    const updateLegacyAtoms = useRecoilCallback(
        ({ set, snapshot }) =>
            (changes: DomainChange[]) => {
                if (!session) {
                    return;
                }

                for (const c of changes) {
                    if (c.kind === 'structureSet') {
                        if (c.type === 'deleted') {
                            // Set to undefined so downstream selectors treat it
                            // as absent (matches the atom's default: undefined).
                            domainTrace.bridge('atom update', {
                                family: 'structureSetReadModelFamily',
                                ssDicomUid: c.snapshot.sopInstanceUid,
                                type: c.type,
                            });
                            set(
                                structureSetReadModelFamily(
                                    c.snapshot.sopInstanceUid
                                ),
                                undefined
                            );
                            // If the deleted SS was the currently
                            // selected one, clear the id so direct
                            // readers (export step, dataset list)
                            // don't hold a dangling UID.
                            const currentId = snapshot
                                .getLoadable(currentStructureSetIdState)
                                .getValue();
                            if (currentId === c.snapshot.sopInstanceUid) {
                                set(currentStructureSetIdState, undefined);
                            }
                        } else {
                            // 'created' | 'modified': resolve live entity and
                            // re-derive the full read model.
                            const ss = session.patient.getStructureSetById(
                                c.id
                            );
                            if (!ss) {
                                continue;
                            }
                            publishReadModel(set, ss, c.type);
                        }
                    } else if (c.kind === 'roi') {
                        // ROI changes carry no direct reference to the parent
                        // StructureSet. Scan all structure sets to find the
                        // owner, then rebuild its full read model (which
                        // includes the roiArray).
                        for (const ss of session.patient.structureSets) {
                            if (ss.getRoiById(c.id)) {
                                publishReadModel(set, ss, c.type);
                                break;
                            }
                        }
                    }
                    // 'patient' kind has no legacy atom to update.
                }
            },
        [session]
    );

    const publishStructureSets = useRecoilCallback(
        ({ set }) =>
            (structureSets: readonly StructureSet[]) => {
                for (const ss of structureSets) {
                    publishReadModel(set, ss, 'attached');
                }
            },
        []
    );

    useEffect(() => {
        if (!session) {
            return;
        }
        publishStructureSets(session.patient.structureSets);
        return session.context.store.onChangeset(updateLegacyAtoms);
    }, [session, updateLegacyAtoms, publishStructureSets]);

    return null;
}
